/**
 * 宠物战机：规则核心（B，pet_arcade/core；合同 docs/coordination/PET-ARCADE-FLIGHT-RULES-B-2026-10-01.md）。
 *
 * 单人短局：手指拖着飞机闪避（单手），飞机自动射击；敌机与障碍按波次从上往下来，炮台会瞄着飞机打慢弹。
 * 连着击落攒连击，击落攒绝招，满了按一下清屏。被撞扣心、短暂无敌；心用完提前结束，撑满时间就是通关。
 * 竖屏场地，逻辑单位 360×640，y 向下。纯 TypeScript：不碰 DOM、计时器、Math.random；固定步进＋种子随机，同种子同输入逐字相同。
 * 失败只是这一局的结果：不产生宠物真实受伤、驾驶旅程或世界记录。
 */

import { createFixedStepClock, STEP_MS } from "./loop";
import { createEventStamper, type Stamped } from "./events";
import { createRng } from "./rng";

export type FlightInput = { kind: "idle" } | { kind: "target"; x: number; y: number } | { kind: "stick"; dx: number; dy: number };
export type EnemyKind = "drone" | "zigzag" | "rock" | "turret";

export interface SpawnSpec { atMs: number; kind: EnemyKind; x: number } // atMs：从开局算
export interface FlightConfig {
  durationMs: number; hearts: number; seed: number;
  /** 自动射击（默认开；关掉给教学关与用例）。 */
  autoFire?: boolean;
  /** 出怪表（给关卡设计与用例）；不给就按种子生成 5 波。 */
  spawns?: readonly SpawnSpec[];
}

export const FLIGHT_WIDTH = 360;
export const FLIGHT_HEIGHT = 640;
export const PLANE_R = 16;
export const PLANE_MIN_Y = 380;
export const PLANE_MAX_Y = 610;
export const PLANE_SPEED = 420; // 每秒单位
export const FLIGHT_START = { x: 180, y: 540 } as const;
export const FIRE_MS = 180;
export const BULLET_SPEED = 700;
export const BULLET_R = 4;
export const PELLET_SPEED = 200;
export const PELLET_R = 5;
export const TURRET_FIRE_MS = 1200;
export const TURRET_HOLD_Y = 140;
export const TURRET_HOLD_MS = 6000;
export const FLIGHT_INVULNERABLE_MS = 1500;
export const FLIGHT_NEAR_MISS_MARGIN = 12;
export const COMBO_WINDOW_MS = 1500;
export const COMBO_BONUS_FROM = 5; // 连击第 5 个起每击落一个 +5
export const COMBO_BONUS = 5;
export const SPECIAL_KILLS = 10; // 攒满 10 个击落可放一次绝招
export const HEART_BONUS = 50;
export const WAVE_MS = 12_000;
export const ENEMY: Record<EnemyKind, { hp: number; radius: number; speed: number; points: number }> = {
  drone: { hp: 1, radius: 14, speed: 110, points: 10 },
  zigzag: { hp: 2, radius: 14, speed: 90, points: 20 },
  rock: { hp: 3, radius: 22, speed: 60, points: 15 },
  turret: { hp: 3, radius: 18, speed: 80, points: 30 },
};
const ZIGZAG_AMPLITUDE = 40;
const ZIGZAG_PERIOD_MS = 1600;

export type FlightPhase = "ready" | "running" | "paused" | "ended";
export type FlightEndReason = "time_up" | "out_of_hearts" | "quit";

export interface Enemy { id: string; kind: EnemyKind; x: number; y: number; hp: number; radius: number }
export interface Shot { id: string; x: number; y: number; radius: number }

export interface FlightResult {
  reason: FlightEndReason; counted: boolean; cleared: boolean; score: number; durationMs: number;
  kills: number; bestCombo: number; hits: number; heartsLeft: number; specials: number; wave: number;
}

export interface FlightState {
  phase: FlightPhase; seed: number; elapsedMs: number; remainingMs: number; hearts: number; score: number;
  combo: number; special: number; wave: number; // special：0～1，满了可放
  plane: { x: number; y: number; radius: number; invulnerableMs: number };
  bullets: readonly Shot[]; pellets: readonly Shot[]; enemies: readonly Enemy[];
  result: FlightResult | null;
}

export type FlightEventBody =
  | { type: "started"; seed: number; durationMs: number; hearts: number }
  | { type: "wave_started"; wave: number }
  | { type: "enemy_hit"; enemyId: string; hpLeft: number }
  | { type: "enemy_destroyed"; enemyId: string; kind: EnemyKind; points: number; combo: number }
  | { type: "combo"; count: number } // 连击到 5、10、15……各发一次
  | { type: "hit"; by: "enemy" | "pellet"; id: string; heartsLeft: number }
  | { type: "near_miss"; id: string }
  | { type: "special_ready" }
  | { type: "special"; cleared: number; points: number }
  | { type: "paused"; elapsedMs: number }
  | { type: "resumed"; elapsedMs: number }
  | { type: "ended"; reason: FlightEndReason; counted: boolean; result: FlightResult };

/** 对外的规则事件：事件体＋唯一标识 (round, step, seq)（动作清单 F0 修订 R2，见 events.ts）。 */
export type FlightEvent = Stamped<FlightEventBody>;

export interface FlightGame {
  readonly state: FlightState;
  setInput(input: FlightInput): void;
  advance(frameMs: number): FlightEvent[];
  /** 放绝招：攒满了才生效，立即结算（清掉场上全部敌机与慢弹、照常计分），返回事件；没满返回空数组。 */
  special(): FlightEvent[];
  start(): FlightEvent[];
  pause(): FlightEvent[];
  resume(): FlightEvent[];
  quit(): FlightEvent[];
  restart(seed?: number): FlightEvent[];
}

export const DEFAULT_FLIGHT_CONFIG: FlightConfig = { durationMs: 60_000, hearts: 3, seed: 1 };

const KIND_WEIGHTS: Array<[EnemyKind, number]> = [["drone", 5], ["zigzag", 3], ["rock", 2], ["turret", 1]];

/** 按种子生成的出怪表：5 波，第 n 波 4＋2n 个，在每波前 9 秒里均匀排开。 */
export function generateSpawns(seed: number, durationMs: number): SpawnSpec[] {
  const rng = createRng(seed ^ 0x5eed);
  const total = KIND_WEIGHTS.reduce((sum, [, w]) => sum + w, 0);
  const spawns: SpawnSpec[] = [];
  for (let wave = 0; wave * WAVE_MS < durationMs; wave++) {
    const count = 4 + 2 * (wave + 1);
    for (let i = 0; i < count; i++) {
      let roll = rng.next() * total;
      const kind = (KIND_WEIGHTS.find(([, w]) => (roll -= w) < 0) ?? KIND_WEIGHTS[0])[0];
      spawns.push({ atMs: wave * WAVE_MS + Math.round((i * 9000) / count), kind, x: Math.round(rng.range(30, FLIGHT_WIDTH - 30)) });
    }
  }
  return spawns;
}

function overlap(ax: number, ay: number, ar: number, bx: number, by: number, br: number, margin = 0): boolean {
  const dx = ax - bx;
  const dy = ay - by;
  const r = ar + br + margin;
  return dx * dx + dy * dy < r * r;
}

export function createFlightGame(overrides: Partial<FlightConfig> = {}): FlightGame {
  const config: FlightConfig = { ...DEFAULT_FLIGHT_CONFIG, ...overrides };
  const clock = createFixedStepClock();
  const stamper = createEventStamper();
  const durationSteps = Math.round(config.durationMs / STEP_MS);
  const dt = STEP_MS / 1000;
  let seed = config.seed;
  let schedule: SpawnSpec[];
  let nextSpawn: number;
  let phase: FlightPhase;
  let steps: number;
  let hearts: number;
  let score: number;
  let combo: number;
  let lastKillMs: number;
  let kills: number;
  let specialReadySent: boolean;
  let plane: FlightState["plane"];
  let fireMs: number;
  let serial: number;
  let bullets: Shot[];
  let pellets: Shot[];
  let enemies: Array<Enemy & { x0: number; bornMs: number; heldMs: number; fireMs: number }>;
  let nearing: Set<string>; // 进了擦身圈、还没碰上的：离开擦身圈那一步（飞机不在无敌期）才算「擦身而过」
  let touched: Set<string>; // 这一趟碰上过的：离开擦身圈之前都不再算擦身
  let wave: number;
  let input: FlightInput;
  let stats: { kills: number; bestCombo: number; hits: number; specials: number };
  let result: FlightResult | null;
  let snapshot: FlightState;

  const nowMs = () => steps * STEP_MS;
  const specialLevel = () => Math.min(1, kills / SPECIAL_KILLS);

  function build(): FlightState {
    return {
      phase, seed, elapsedMs: nowMs(), remainingMs: Math.max(0, config.durationMs - nowMs()), hearts, score, combo, special: specialLevel(), wave,
      plane: { ...plane }, bullets: bullets.map((b) => ({ ...b })), pellets: pellets.map((p) => ({ ...p })),
      enemies: enemies.map(({ id, kind, x, y, hp, radius }) => ({ id, kind, x, y, hp, radius })), result,
    };
  }

  function reset(newSeed: number): void {
    seed = newSeed;
    schedule = [...(config.spawns ?? generateSpawns(newSeed, config.durationMs))].sort((a, b) => a.atMs - b.atMs);
    nextSpawn = 0;
    clock.reset();
    phase = "ready";
    steps = 0;
    hearts = config.hearts;
    score = 0;
    combo = 0;
    lastKillMs = -Infinity;
    kills = 0;
    specialReadySent = false;
    plane = { x: FLIGHT_START.x, y: FLIGHT_START.y, radius: PLANE_R, invulnerableMs: 0 };
    fireMs = 0;
    serial = 0;
    bullets = [];
    pellets = [];
    enemies = [];
    nearing = new Set();
    touched = new Set();
    wave = 0;
    input = { kind: "idle" };
    stats = { kills: 0, bestCombo: 0, hits: 0, specials: 0 };
    result = null;
    snapshot = build();
  }

  function finish(reason: FlightEndReason, events: FlightEventBody[]): void {
    phase = "ended";
    if (reason === "time_up") score += hearts * HEART_BONUS;
    result = { reason, counted: reason !== "quit", cleared: reason === "time_up", score, durationMs: nowMs(), kills: stats.kills,
      bestCombo: stats.bestCombo, hits: stats.hits, heartsLeft: hearts, specials: stats.specials, wave };
    events.push({ type: "ended", reason, counted: result.counted, result });
  }

  function movePlane(): void {
    const perStep = PLANE_SPEED * dt;
    let vx = 0;
    let vy = 0;
    if (input.kind === "target") {
      const dx = input.x - plane.x;
      const dy = input.y - plane.y;
      const dist = Math.hypot(dx, dy);
      if (dist >= 0.5) {
        const move = Math.min(perStep, dist);
        vx = (dx / dist) * move;
        vy = (dy / dist) * move;
      }
    } else if (input.kind === "stick") {
      const len = Math.hypot(input.dx, input.dy);
      if (len >= 0.05) {
        const power = Math.min(1, len);
        vx = (input.dx / len) * power * perStep;
        vy = (input.dy / len) * power * perStep;
      }
    }
    plane.x = Math.max(PLANE_R, Math.min(FLIGHT_WIDTH - PLANE_R, plane.x + vx));
    plane.y = Math.max(PLANE_MIN_Y, Math.min(PLANE_MAX_Y, plane.y + vy));
  }

  function kill(enemy: Enemy, events: FlightEventBody[]): number {
    combo += 1; // 超过窗口没击落的，每步开头已经清零
    lastKillMs = nowMs();
    const points = ENEMY[enemy.kind].points + (combo >= COMBO_BONUS_FROM ? COMBO_BONUS : 0);
    score += points;
    stats.kills += 1;
    stats.bestCombo = Math.max(stats.bestCombo, combo);
    kills += 1; // 绝招清掉的也数，但放完绝招整个归零
    events.push({ type: "enemy_destroyed", enemyId: enemy.id, kind: enemy.kind, points, combo });
    if (combo % COMBO_BONUS_FROM === 0) events.push({ type: "combo", count: combo });
    if (!specialReadySent && specialLevel() >= 1) {
      specialReadySent = true;
      events.push({ type: "special_ready" });
    }
    return points;
  }

  function hurt(by: "enemy" | "pellet", id: string, events: FlightEventBody[]): boolean {
    hearts -= 1;
    stats.hits += 1;
    plane.invulnerableMs = FLIGHT_INVULNERABLE_MS;
    events.push({ type: "hit", by, id, heartsLeft: hearts });
    if (hearts <= 0) {
      finish("out_of_hearts", events);
      return true;
    }
    return false;
  }

  function step(events: FlightEventBody[]): void {
    steps += 1;
    const t = nowMs();
    if (plane.invulnerableMs > 0) plane.invulnerableMs = Math.max(0, plane.invulnerableMs - STEP_MS);
    if (combo > 0 && t - lastKillMs > COMBO_WINDOW_MS) combo = 0; // 断连击：超过窗口没击落（只这一处判窗口）
    // 波次与出怪
    const currentWave = Math.min(Math.floor((t - STEP_MS) / WAVE_MS) + 1, Math.ceil(config.durationMs / WAVE_MS));
    if (currentWave > wave) {
      wave = currentWave;
      events.push({ type: "wave_started", wave });
    }
    while (nextSpawn < schedule.length && schedule[nextSpawn].atMs <= t) {
      const spec = schedule[nextSpawn++];
      const base = ENEMY[spec.kind];
      enemies.push({ id: `e-${++serial}`, kind: spec.kind, x: spec.x, y: -base.radius, hp: base.hp, radius: base.radius, x0: spec.x, bornMs: t, heldMs: 0,
        fireMs: TURRET_FIRE_MS });
    }
    movePlane();
    // 自动射击
    fireMs -= STEP_MS;
    if (fireMs <= 0 && config.autoFire !== false) {
      fireMs += FIRE_MS;
      bullets.push({ id: `b-${++serial}`, x: plane.x, y: plane.y - PLANE_R, radius: BULLET_R });
    }
    for (const b of bullets) b.y -= BULLET_SPEED * dt;
    for (const p of pellets) {
      const q = p as Shot & { vx: number; vy: number };
      q.x += q.vx * dt;
      q.y += q.vy * dt;
    }
    // 敌人移动与炮台开火
    for (const e of enemies) {
      const base = ENEMY[e.kind];
      if (e.kind === "zigzag") {
        e.y += base.speed * dt;
        e.x = e.x0 + ZIGZAG_AMPLITUDE * Math.sin(((t - e.bornMs) / ZIGZAG_PERIOD_MS) * 2 * Math.PI);
      } else if (e.kind === "turret" && e.y >= TURRET_HOLD_Y && e.heldMs < TURRET_HOLD_MS) {
        e.heldMs += STEP_MS;
        e.fireMs -= STEP_MS;
        if (e.fireMs <= 0) {
          e.fireMs += TURRET_FIRE_MS;
          const dx = plane.x - e.x;
          const dy = plane.y - e.y;
          const len = Math.hypot(dx, dy) || 1;
          pellets.push(Object.assign({ id: `p-${++serial}`, x: e.x, y: e.y + e.radius, radius: PELLET_R }, { vx: (dx / len) * PELLET_SPEED, vy: (dy / len) * PELLET_SPEED }));
        }
      } else {
        e.y += base.speed * dt;
      }
    }
    // 子弹打敌人
    for (const b of [...bullets]) {
      const target = enemies.find((e) => overlap(b.x, b.y, b.radius, e.x, e.y, e.radius));
      if (!target) continue;
      bullets = bullets.filter((x) => x !== b);
      target.hp -= 1;
      if (target.hp > 0) events.push({ type: "enemy_hit", enemyId: target.id, hpLeft: target.hp });
      else {
        enemies = enemies.filter((e) => e !== target);
        kill(target, events);
      }
    }
    // 撞击与擦身而过
    const threats: Array<{ by: "enemy" | "pellet"; id: string; x: number; y: number; r: number }> = [
      ...enemies.map((e) => ({ by: "enemy" as const, id: e.id, x: e.x, y: e.y, r: e.radius })),
      ...pellets.map((p) => ({ by: "pellet" as const, id: p.id, x: p.x, y: p.y, r: p.radius })),
    ];
    for (const th of threats) {
      if (overlap(plane.x, plane.y, plane.radius, th.x, th.y, th.r)) {
        nearing.delete(th.id);
        touched.add(th.id);
        if (plane.invulnerableMs === 0) {
          if (th.by === "enemy") enemies = enemies.filter((e) => e.id !== th.id); // 撞上的敌机一起报废（不计分）
          else pellets = pellets.filter((p) => p.id !== th.id);
          if (hurt(th.by, th.id, events)) return;
        }
      } else if (overlap(plane.x, plane.y, plane.radius, th.x, th.y, th.r, FLIGHT_NEAR_MISS_MARGIN)) {
        if (!touched.has(th.id)) nearing.add(th.id);
      } else {
        if (nearing.has(th.id) && plane.invulnerableMs === 0) events.push({ type: "near_miss", id: th.id }); // 过去了、全程没碰上
        nearing.delete(th.id);
        touched.delete(th.id);
      }
    }
    // 出界清理
    bullets = bullets.filter((b) => b.y > -b.radius);
    pellets = pellets.filter((p) => p.y < FLIGHT_HEIGHT + p.radius && p.y > -p.radius && p.x > -p.radius && p.x < FLIGHT_WIDTH + p.radius);
    enemies = enemies.filter((e) => e.y < FLIGHT_HEIGHT + e.radius);
    const alive = new Set([...enemies.map((e) => e.id), ...pellets.map((p) => p.id)]);
    for (const id of nearing) if (!alive.has(id)) nearing.delete(id); // 被击落、被清掉、飞出场地的：没「过去」，不算擦身
    for (const id of touched) if (!alive.has(id)) touched.delete(id);
    if (steps >= durationSteps) finish("time_up", events);
  }

  reset(seed);

  return {
    get state() {
      return snapshot;
    },
    setInput(next: FlightInput): void {
      input = next;
    },
    special(): FlightEvent[] {
      if (phase !== "running" || specialLevel() < 1) return [];
      const events: FlightEventBody[] = [];
      const cleared = enemies.length + pellets.length;
      let points = 0;
      for (const e of [...enemies]) points += kill(e, events);
      enemies = [];
      pellets = [];
      nearing.clear();
      touched.clear();
      kills = 0;
      specialReadySent = false;
      stats.specials += 1;
      events.push({ type: "special", cleared, points });
      snapshot = build();
      return stamper.stamp(events, steps);
    },
    start(): FlightEvent[] {
      if (phase !== "ready") return [];
      phase = "running";
      snapshot = build();
      return stamper.stamp<FlightEventBody>([{ type: "started", seed, durationMs: config.durationMs, hearts }], steps);
    },
    advance(frameMs: number): FlightEvent[] {
      if (phase !== "running") return [];
      const stamped: FlightEvent[] = [];
      const n = clock.feed(frameMs);
      for (let i = 0; i < n && phase === "running"; i++) {
        const body: FlightEventBody[] = [];
        step(body);
        stamped.push(...stamper.stamp(body, steps));
      }
      snapshot = build();
      return stamped;
    },
    pause(): FlightEvent[] {
      if (phase !== "running") return [];
      phase = "paused";
      clock.reset();
      snapshot = build();
      return stamper.stamp<FlightEventBody>([{ type: "paused", elapsedMs: nowMs() }], steps);
    },
    resume(): FlightEvent[] {
      if (phase !== "paused") return [];
      phase = "running";
      snapshot = build();
      return stamper.stamp<FlightEventBody>([{ type: "resumed", elapsedMs: nowMs() }], steps);
    },
    quit(): FlightEvent[] {
      if (phase !== "running" && phase !== "paused") return [];
      const events: FlightEventBody[] = [];
      finish("quit", events);
      snapshot = build();
      return stamper.stamp(events, steps);
    },
    restart(newSeed?: number): FlightEvent[] {
      reset(newSeed ?? seed);
      stamper.nextRound();
      phase = "running";
      snapshot = build();
      return stamper.stamp<FlightEventBody>([{ type: "started", seed, durationMs: config.durationMs, hearts }], steps);
    },
  };
}
